import { logAnalyticsEvent, ANALYTICS_EVENTS, type AnalyticsEventType, type AnalyticsEventParams } from '@/utils/Analytics';
import { errorMonitoring } from '@/services/errorMonitoring';

// Error impact categories for better prioritization
type ErrorImpact = 'critical' | 'high' | 'medium' | 'low';

// Benign browser noise that should never be tracked or alerted on. These inflate
// js_error/error_occurred counts without representing a real product failure:
//  - ResizeObserver loop warnings (fired by the browser, harmless)
//  - cross-origin "Script error." (no stack/detail; usually a third-party/extension)
//  - "Object Not Found Matching Id:N, MethodName:update" is Microsoft Defender
//    SafeLinks (and similar email-security scanners) opening our email links in an
//    instrumented browser whose own injected script throws. Rollbar already ignores
//    it (errorMonitoring.ts); GA4 did not, so every letter to a corporate inbox
//    showed up as a js_error spike on /signin, /unsubscribe, /trial-signup
//    (103 of 113 error events on 2026-09-05, the day after two sends).
const NOISE_PATTERNS: RegExp[] = [
  /resizeobserver loop/i,
  /^script error\.?$/i,
  /object not found matching id/i,
];

const isNoiseError = (error: Error | null | undefined, rawMessage?: string): boolean => {
  const message = (error?.message ?? rawMessage ?? '').trim();
  if (!message) return true; // nothing actionable to report
  return NOISE_PATTERNS.some((pattern) => pattern.test(message));
};

interface ErrorContext {
  type: 'js' | 'api' | 'network' | 'other';
  component?: string;
  action?: string;
  metadata?: Partial<AnalyticsEventParams['error_occurred']>;
}

/**
 * Smart error categorization based on user impact
 * Helps prioritize fixes by business importance
 */
const getErrorEventName = (error: Error, context: ErrorContext): AnalyticsEventType => {
  const component = context.component?.toLowerCase() || '';
  const action = context.action?.toLowerCase() || '';
  const message = error.message.toLowerCase();

  // 🔥 CRITICAL: Blocks core user journeys
  if (component.includes('quiz') || component.includes('question')) {
    if (action.includes('load') || action.includes('start')) return ANALYTICS_EVENTS.QUIZ_LOAD_FAILED;
    if (action.includes('submit') || action.includes('answer')) return ANALYTICS_EVENTS.QUESTION_SUBMIT_FAILED;
    return ANALYTICS_EVENTS.QUIZ_ERROR;
  }

  if (component.includes('payment') || component.includes('stripe') || component.includes('subscription')) {
    return ANALYTICS_EVENTS.PAYMENT_FAILED;
  }

  if (component.includes('auth') || component.includes('signin') || component.includes('login')) {
    return ANALYTICS_EVENTS.SIGNIN_FAILED;
  }

  // 📚 HIGH: Feature-specific errors that impact learning
  if (component.includes('flashcard')) {
    if (action.includes('load') || action.includes('start')) return ANALYTICS_EVENTS.FLASHCARD_LOAD_FAILED;
    return ANALYTICS_EVENTS.FLASHCARD_ERROR;
  }

  if (action.includes('sync') || message.includes('sync') || message.includes('save')) {
    return ANALYTICS_EVENTS.PROGRESS_SYNC_FAILED;
  }

  if (component.includes('content') || message.includes('question') || message.includes('content')) {
    return ANALYTICS_EVENTS.CONTENT_LOAD_FAILED;
  }

  // 🚀 MEDIUM: Performance issues
  if (message.includes('timeout') || message.includes('slow') || action.includes('timeout')) {
    if (context.type === 'api') return ANALYTICS_EVENTS.API_TIMEOUT;
    return ANALYTICS_EVENTS.PAGE_LOAD_TIMEOUT;
  }

  if (message.includes('memory') || message.includes('heap')) {
    return ANALYTICS_EVENTS.MEMORY_ERROR;
  }

  // 🔧 LOW: Technical errors (existing categories)
  if (context.type === 'api') return ANALYTICS_EVENTS.API_ERROR;
  if (context.type === 'network') return ANALYTICS_EVENTS.NETWORK_ERROR;
  if (context.type === 'js') return ANALYTICS_EVENTS.JS_ERROR;

  // Fallback
  return ANALYTICS_EVENTS.ERROR_OCCURRED;
};

/**
 * Get error impact level for alerting and prioritization
 */
const getErrorImpact = (eventName: AnalyticsEventType): ErrorImpact => {
  const criticalErrors: AnalyticsEventType[] = [
    ANALYTICS_EVENTS.QUIZ_LOAD_FAILED,
    ANALYTICS_EVENTS.PAYMENT_FAILED,
    ANALYTICS_EVENTS.SIGNIN_FAILED,
    ANALYTICS_EVENTS.QUESTION_SUBMIT_FAILED,
  ];
  const highErrors: AnalyticsEventType[] = [
    ANALYTICS_EVENTS.FLASHCARD_LOAD_FAILED,
    ANALYTICS_EVENTS.PROGRESS_SYNC_FAILED,
    ANALYTICS_EVENTS.CONTENT_LOAD_FAILED,
  ];
  const mediumErrors: AnalyticsEventType[] = [
    ANALYTICS_EVENTS.QUIZ_ERROR,
    ANALYTICS_EVENTS.FLASHCARD_ERROR,
    ANALYTICS_EVENTS.API_TIMEOUT,
    ANALYTICS_EVENTS.PAGE_LOAD_TIMEOUT,
  ];

  if (criticalErrors.includes(eventName)) return 'critical';
  if (highErrors.includes(eventName)) return 'high';
  if (mediumErrors.includes(eventName)) return 'medium';
  return 'low';
};

export const trackError = (
  error: Error,
  context: ErrorContext
) => {
  // Defensive: the browser can hand us a null/non-Error (e.g. cross-origin
  // window.onerror), and a thrown error inside the error handler is the worst
  // kind of noise. Coerce to a real Error before touching `.message`/`.stack`.
  const safeError = error instanceof Error
    ? error
    : new Error(String((error as { message?: string } | null)?.message ?? error ?? 'Unknown error'));

  // Drop benign browser noise so it never reaches analytics or Rollbar.
  if (isNoiseError(safeError)) return;

  // Get specific error event name based on impact
  const eventName = getErrorEventName(safeError, context);
  const impact = getErrorImpact(eventName);

  const errorData = {
    error_message: safeError.message,
    error_type: context.type,
    stack_trace: safeError.stack,
    timestamp: new Date().toISOString(),
    impact_level: impact,
    error_category: eventName,
    user_journey_impact: impact === 'critical' ? 'blocks_core_flow' :
                         impact === 'high' ? 'degrades_experience' : 'minor_issue',
    component: context.component,
    action: context.action,
    ...(context.metadata ?? {})
  };

  // Emit ONLY the granular event here (js_error, quiz_load_failed, …). The
  // generic `error_occurred` roll-up is owned solely by errorMonitoring.logError
  // below — emitting it here too is what double/triple-counted every error.
  if (eventName !== ANALYTICS_EVENTS.ERROR_OCCURRED) {
    logAnalyticsEvent(eventName, errorData as Parameters<typeof logAnalyticsEvent>[1]);
  }

  // Log to error monitoring service (Rollbar + the single error_occurred roll-up)
  errorMonitoring.logError(safeError, {
    component: context.component,
    action: context.action,
    level: impact === 'critical' ? 'critical' : impact === 'high' ? 'error' : 'warning',
    metadata: {
      error_type: context.type,
      error_category: eventName,
      impact_level: impact,
      ...(context.metadata ?? {})
    }
  });
};

export const initializeErrorTracking = () => {
  // Initialize error monitoring service
  errorMonitoring.initialize();

  // Set up global error handlers
  window.addEventListener('error', (event) => {
    // Cross-origin errors arrive with event.error === null and a generic
    // "Script error." message — filter before synthesizing a usable Error.
    if (isNoiseError(event.error, event.message)) return;
    const error = event.error instanceof Error
      ? event.error
      : new Error(event.message || 'Uncaught error');
    trackError(error, {
      type: 'js',
      component: 'window',
      action: 'uncaught_error',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    if (isNoiseError(error)) return;
    trackError(error, {
      type: 'js',
      component: 'window',
      action: 'unhandled_promise',
      metadata: {
        reason: String(event.reason)
      }
    });
  });
}; 